
import { computed, defineComponent, reactive } from 'vue';
import { datetimeFormat } from '@/utils';
import dayjs from 'dayjs';
import CouponPrice from '@/components/molecules/coupon/CouponPrice.vue';

export default defineComponent({
  name: 'CustomerCouponDetail',

  props: {
    customerCoupon: {
      type: Object,
      required: true,
    },
    restriction: {
      type: String,
      required: false,
    },
  },

  components: {
    CouponPrice,
  },

  setup(props) {
    const state = reactive({
      used: props.customerCoupon.used,
      usedAt: datetimeFormat(props.customerCoupon.updatedAt, 'datetime'),
    });

    const name = computed(() => props.customerCoupon.couponMaster.name);

    const overview = computed(() => {
      try {
        return JSON.parse(props.customerCoupon.couponMaster.attrs).overview || null;
      } catch {
        return null;
      }
    });

    const mainImageUrl = computed(() => {
      try {
        return JSON.parse(props.customerCoupon.couponMaster.attrs).mainImageUrl || null;
      } catch {
        return null;
      }
    });

    /**
     * CS-9138 画像アスペクト比を変更可能に
     */
    const imageAspect = computed(() => {
      try {
        const str = JSON.parse(props.customerCoupon.couponMaster.attrs);
        const aspect = str.imageAspect || '16 / 9';
        return aspect.replace(' / ', 'by');
      } catch {
        return '16by9';
      }
    });

    const discount = computed(() => JSON.parse(props.customerCoupon.couponMaster.attrs).discount);

    const hasDiscount = computed(() => {
      if (discount.value.type === 'FREE') return false;
      if (discount.value.type === 'CONST' && discount.value.value === 0) return false;
      if (discount.value.type === 'RATE' && discount.value.value === 0) return false;
      return true;
    });

    const expiresAt = computed(() => {
      // 静的有効期限
      if (props.customerCoupon.expiresAt) return datetimeFormat(props.customerCoupon.expiresAt);

      // 動的有効期限
      const expires = JSON.parse(props.customerCoupon.couponMaster.attrs).expires;
      if (expires.startAt && expires.endAt)
        return `${datetimeFormat(expires.startAt)}<br />〜 ${datetimeFormat(expires.endAt)}`;
      if (expires.startAt) return `${datetimeFormat(expires.startAt)} 〜`;
      if (expires.endAt) return `〜 ${datetimeFormat(expires.endAt)}`;

      return 'なし'; // 有効期限なし
    });

    const expired = computed(() => {
      const { expires: expiresInfo } = JSON.parse(props.customerCoupon.couponMaster.attrs) as {
        expires: { term: number | null; startAt: string | null; endAt: string | null };
      };
      const now = Date.now();
      if (props.customerCoupon.expiresAt) {
        return new Date(props.customerCoupon.expiresAt).getTime() <= now;
      } else if (expiresInfo.endAt) {
        return new Date(expiresInfo.endAt).getTime() <= now;
      }

      return false;
    });

    /**
     * （明示的に）使用済みスタンプを押す
     * @public
     */
    const stamp = () => {
      state.used = true;
      state.usedAt = datetimeFormat(dayjs().toISOString());
    };

    return {
      state,
      name,
      overview,
      mainImageUrl,
      imageAspect, // CS-9138
      discount,
      hasDiscount,
      expiresAt,
      expired,
      stamp,
    };
  },
});
